import {
  deepFreeze,
  type ArenaMap,
  type Balance,
  type Camp,
  type LoadedContent,
} from '@orbomb/content';
import { SIMULATION_VERSION, TICK_SECONDS, secondsToTicks } from './constants';
import { degToRad, detExp } from './detmath';

export interface SolidRect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
  readonly kind: 'BLOCK' | 'BASE';
  readonly owner: Camp | null;
  readonly id: string;
}

/**
 * Everything the simulation needs, derived once from validated content and frozen.
 * Durations are integer ticks; per-tick factors are precomputed deterministically.
 */
export interface SimConfig {
  readonly simulationVersion: string;
  readonly contentHash: string;
  readonly balanceHash: string;
  readonly map: ArenaMap;
  readonly balance: Balance;
  readonly columns: number;
  readonly rows: number;
  readonly solids: readonly SolidRect[];
  readonly ticks: {
    readonly fuse: number;
    readonly clearPreview: number;
    readonly chainDelay: number;
    readonly lethal: number;
    readonly visual: number;
    readonly stun: number;
    readonly protection: number;
    readonly absent: number;
    readonly materialise: number;
    readonly countdown: number;
    readonly rematchCountdown: number;
    readonly regulation: number;
    readonly suddenDeathAutoArmAfter: number;
    readonly suddenDeathAutoArmInterval: number;
    readonly suddenDeathUnlimitedRangeAfter: number;
    readonly bonusInterval: number;
    readonly bonusLifetime: number;
  };
  readonly player: {
    readonly radius: number;
    readonly inverseMass: number;
    readonly accelerationPerTick: number;
    readonly maxInputSpeed: number;
    readonly dampingFactor: number;
    readonly hardSpeedCap: number;
    readonly wallRestitution: number;
    readonly playerRestitution: number;
    readonly tangentialFriction: number;
  };
  readonly golden: {
    readonly count: number;
    readonly radius: number;
    readonly inverseMass: number;
    readonly cruiseSpeed: number;
    readonly maxSpeed: number;
    readonly restitution: number;
    readonly playerContactRestitution: number;
    readonly overspeedFactor: number;
    readonly underspeedLerp: number;
    readonly reboundJitterRadians: number;
    readonly minAxisAngleDegrees: number;
  };
  readonly explosion: {
    readonly range: number;
    readonly unlimitedRange: number;
    readonly halfWidth: number;
    readonly neutralEjectionSpeed: number;
  };
  readonly respawn: {
    readonly minPlayerDistance: number;
  };
  readonly bonus: {
    readonly radius: number;
  };
  readonly totemHp: number;
  /** Lives a totem can reach with 1Up bonuses. */
  readonly totemMaxHp: number;
}

function buildSolids(map: ArenaMap): SolidRect[] {
  const inset = (1 - map.blockCollisionSize) / 2;
  const solids: SolidRect[] = map.blocks.map((b) => ({
    x0: b.col + inset,
    y0: b.row + inset,
    x1: b.col + 1 - inset,
    y1: b.row + 1 - inset,
    kind: 'BLOCK' as const,
    owner: null,
    id: `block-${b.col}-${b.row}`,
  }));
  for (const camp of ['ORANGE', 'TURQUOISE'] as const) {
    const cells = map.bases[camp];
    const minCol = Math.min(...cells.map((c) => c.col));
    const maxCol = Math.max(...cells.map((c) => c.col));
    const row = cells[0]?.row ?? 0;
    solids.push({
      x0: minCol,
      y0: row,
      x1: maxCol + 1,
      y1: row + 1,
      kind: 'BASE',
      owner: camp,
      id: `base-${camp.toLowerCase()}`,
    });
  }
  return solids;
}

export function createSimConfig(content: LoadedContent): SimConfig {
  const { map, balance } = content;
  const b = balance;
  const config: SimConfig = {
    simulationVersion: SIMULATION_VERSION,
    contentHash: content.contentHash,
    balanceHash: content.balanceHash,
    map,
    balance,
    columns: map.columns,
    rows: map.rows,
    solids: buildSolids(map),
    ticks: {
      fuse: secondsToTicks(b.fuse.fuseSeconds, 'fuse'),
      clearPreview: secondsToTicks(b.fuse.clearPreviewSeconds, 'clearPreview'),
      chainDelay: secondsToTicks(b.fuse.chainDelaySeconds, 'chainDelay'),
      lethal: secondsToTicks(b.explosion.lethalSeconds, 'lethal'),
      visual: secondsToTicks(b.explosion.visualSeconds, 'visual'),
      stun: secondsToTicks(b.player.stunSeconds, 'stun'),
      protection: secondsToTicks(b.player.protectionSeconds, 'protection'),
      absent: secondsToTicks(b.respawn.absentSeconds, 'absent'),
      materialise: secondsToTicks(b.respawn.materialiseSeconds, 'materialise'),
      countdown: secondsToTicks(b.match.countdownSeconds, 'countdown'),
      rematchCountdown: secondsToTicks(b.match.rematchCountdownSeconds, 'rematchCountdown'),
      regulation: secondsToTicks(b.match.regulationSeconds, 'regulation'),
      suddenDeathAutoArmAfter: secondsToTicks(b.match.suddenDeathAutoArmAfterSeconds, 'sdAutoArm'),
      suddenDeathAutoArmInterval: secondsToTicks(
        b.match.suddenDeathAutoArmIntervalSeconds,
        'sdAutoArmInterval',
      ),
      suddenDeathUnlimitedRangeAfter: secondsToTicks(
        b.match.suddenDeathUnlimitedRangeAfterSeconds,
        'sdUnlimited',
      ),
      bonusInterval: secondsToTicks(b.bonus.intervalSeconds, 'bonusInterval'),
      bonusLifetime: secondsToTicks(b.bonus.lifetimeSeconds, 'bonusLifetime'),
    },
    player: {
      radius: b.player.radius,
      inverseMass: 1 / b.player.mass,
      accelerationPerTick: b.player.acceleration * TICK_SECONDS,
      maxInputSpeed: b.player.maxInputSpeed,
      dampingFactor: detExp(-b.player.damping * TICK_SECONDS),
      hardSpeedCap: b.player.hardSpeedCap,
      wallRestitution: b.player.wallRestitution,
      playerRestitution: b.player.playerRestitution,
      tangentialFriction: b.player.tangentialFriction,
    },
    golden: {
      count: b.golden.count,
      radius: b.golden.radius,
      inverseMass: 1 / b.golden.mass,
      cruiseSpeed: b.golden.cruiseSpeed,
      maxSpeed: b.golden.maxSpeed,
      restitution: b.golden.restitution,
      playerContactRestitution: b.golden.playerContactRestitution,
      overspeedFactor: detExp(-b.golden.overspeedDecayPerSecond * TICK_SECONDS),
      underspeedLerp: 1 - detExp(-b.golden.underspeedRecoveryPerSecond * TICK_SECONDS),
      reboundJitterRadians: degToRad(b.golden.reboundJitterDegrees),
      minAxisAngleDegrees: b.golden.minAxisAngleDegrees,
    },
    explosion: {
      range: b.explosion.range,
      unlimitedRange: Math.max(map.columns, map.rows),
      halfWidth: b.explosion.beamWidth / 2,
      neutralEjectionSpeed: b.explosion.neutralEjectionSpeed,
    },
    respawn: {
      minPlayerDistance: b.respawn.minPlayerDistance,
    },
    bonus: {
      radius: b.bonus.radius,
    },
    totemHp: b.totem.hp,
    totemMaxHp: b.totem.maxHp,
  };
  return deepFreeze(config);
}
